import React, { useState, useEffect } from 'react';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import BrandHeader from '../../components/ui/BrandHeader';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import Button from '../../components/ui/Button';
import ReceiptGenerator from '../payment-management/components/ReceiptGenerator';
import { searchStudents } from '../../services/studentService';
import { getFeeCategories, getCurrentAcademicYear, getStudentFeeStructure } from '../../services/feeService';
import { createPayment, generateReceiptNumber } from '../../services/paymentService';
import { getCurrentUser } from '../../services/authService';

const QuickCashPayment = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const [feeCategories, setFeeCategories] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [studentFees, setStudentFees] = useState([]);
  const [paymentData, setPaymentData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  useEffect(() => {
    loadFeeCategories();
  }, []);

  useEffect(() => {
    if (selectedStudent?.id && currentAcademicYear?.id) {
      loadStudentFees();
    }
  }, [selectedStudent, currentAcademicYear]);

  useEffect(() => {
    const total = Object.values(amounts)?.reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
    setTotalAmount(total);
  }, [amounts]);

  const loadFeeCategories = async () => {
    try {
      const categories = await getFeeCategories();
      setFeeCategories(categories);
      
      const year = await getCurrentAcademicYear();
      setCurrentAcademicYear(year);
    } catch (error) {
      console.error('Error loading fee categories:', error);
    }
  };

  const loadStudentFees = async () => {
    try {
      const fees = await getStudentFeeStructure(selectedStudent?.id, currentAcademicYear?.id);
      setStudentFees(fees);
    } catch (error) {
      console.error('Error loading student fees:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm?.trim()) return;

    setIsSearching(true);
    
    try {
      const results = await searchStudents(searchTerm);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching students:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleSearch();
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setSearchResults([]);
    setSearchTerm('');
    setSelectedTerm('');
    setSelectedCategories([]);
    setAmounts({});
    setErrors({});
  };

  const termOptions = [
    { value: 'term1', label: 'Term 1 (June - September)' },
    { value: 'term2', label: 'Term 2 (October - January)' },
    { value: 'term3', label: 'Term 3 (February - May)' }
  ];

  const availableCategories = feeCategories?.map(cat => ({
    id: cat?.id,
    label: cat?.name,
    amount: studentFees?.find(f => f?.feeCategoryId === cat?.id && f?.term === selectedTerm)?.amount || 0
  }))?.filter(cat => cat?.amount > 0);

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev?.includes(categoryId)) {
        const newAmounts = { ...amounts };
        delete newAmounts?.[categoryId];
        setAmounts(newAmounts);
        return prev?.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleAmountChange = (categoryId, value) => {
    const category = availableCategories?.find(cat => cat?.id === categoryId);
    const numValue = parseFloat(value) || 0;

    if (selectedCategories?.length === 1 && selectedCategories?.[0] === categoryId) {
      if (numValue > category?.amount) {
        setErrors({ ...errors, [categoryId]: `Amount cannot exceed ₹${category?.amount?.toLocaleString('en-IN')}` });
      } else {
        const newErrors = { ...errors };
        delete newErrors?.[categoryId];
        setErrors(newErrors);
      }
      setAmounts({ ...amounts, [categoryId]: value });
    } else {
      setAmounts({ ...amounts, [categoryId]: category?.amount?.toString() });
    }
  };

  const handleSubmit = async () => {
    if (!selectedTerm) {
      setErrors({ term: 'Please select a term' });
      return;
    }

    if (selectedCategories?.length === 0) {
      setErrors({ categories: 'Please select at least one fee category' });
      return;
    }

    if (totalAmount === 0) {
      setErrors({ amount: 'Total amount must be greater than zero' });
      return;
    }

    setIsProcessing(true);
    
    try {
      const user = await getCurrentUser();
      const receiptNumber = generateReceiptNumber();

      for (const catId of selectedCategories) {
        await createPayment({
          studentId: selectedStudent?.id,
          academicYearId: currentAcademicYear?.id,
          term: selectedTerm,
          feeCategoryId: catId,
          amount: parseFloat(amounts?.[catId] || 0),
          paymentMethod: 'cash',
          paymentStatus: 'completed',
          receiptNumber: `${receiptNumber}_${catId}`,
          recordedBy: user?.id
        });
      }

      setPaymentData({
        studentName: selectedStudent?.name,
        admissionNumber: selectedStudent?.admissionNumber,
        totalAmount,
        receiptNumber,
        paymentDate: new Date()?.toISOString()
      });
      setShowReceipt(true);
      
      setSelectedTerm('');
      setSelectedCategories([]);
      setAmounts({});
      setErrors({});
    } catch (error) {
      console.error('Error processing payment:', error);
      setErrors({ submit: 'Failed to process payment. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedStudent(null);
    setPaymentData(null);
  };

  const handleClearStudent = () => {
    setSelectedStudent(null);
    setSelectedTerm('');
    setSelectedCategories([]);
    setAmounts({});
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation />
      
      <div className="lg:ml-64">
        <BrandHeader variant="admin" />
        
        <main className="p-4 md:p-6 lg:p-8 pb-20 lg:pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Zap" size={32} className="text-primary" />
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                  Quick Cash Payment
                </h1>
              </div>
              <p className="text-sm md:text-base text-muted-foreground">
                Fast cash payment entry with instant receipt generation
              </p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Search" size={20} className="text-primary" />
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  Student Search
                </h2>
              </div>
              
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Search by Admission Number, Name, or Mobile"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    disabled={!!selectedStudent}
                  />
                </div>
                <Button
                  variant="default"
                  iconName="Search"
                  iconPosition="left"
                  onClick={handleSearch}
                  loading={isSearching}
                  disabled={!!selectedStudent}
                  className="w-full md:w-auto"
                >
                  Search
                </Button>
              </div>

              {searchResults?.length > 0 && !selectedStudent && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {searchResults?.map((student) => (
                    <div
                      key={student?.id}
                      onClick={() => handleStudentSelect(student)}
                      className="p-3 bg-muted rounded-lg border border-border hover:border-primary hover:shadow-warm cursor-pointer transition-all duration-250"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-sm md:text-base font-medium text-foreground truncate">
                              {student?.name}
                            </h3>
                            <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded whitespace-nowrap">
                              {student?.admissionNumber}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>Class {student?.class}-{student?.section}</span>
                            <span>•</span>
                            <span>{student?.mobile}</span>
                          </div>
                        </div>
                        <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedStudent && (
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Icon name="CreditCard" size={20} className="text-primary" />
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                      Payment Entry
                    </h2>
                  </div>
                  <Button
                    variant="outline"
                    iconName="X"
                    iconPosition="left"
                    onClick={handleClearStudent}
                    className="text-sm"
                  >
                    Clear
                  </Button>
                </div>

                <div className="bg-muted rounded-lg p-3 md:p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-medium text-foreground mb-1">
                        {selectedStudent?.name}
                      </h3>
                      <div className="flex flex-wrap gap-2 md:gap-4 text-xs md:text-sm text-muted-foreground">
                        <span>{selectedStudent?.admissionNumber}</span>
                        <span>Class {selectedStudent?.class}-{selectedStudent?.section}</span>
                        <span className="data-text">Balance: ₹{selectedStudent?.balance?.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 md:space-y-6">
                  <Select
                    label="Select Term"
                    placeholder="Choose payment term"
                    options={termOptions}
                    value={selectedTerm}
                    onChange={setSelectedTerm}
                    error={errors?.term}
                    required
                  />

                  {selectedTerm && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-3">
                        Fee Categories <span className="text-error">*</span>
                      </label>
                      <div className="space-y-2">
                        {availableCategories?.map((category) => (
                          <div key={category?.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                            <Checkbox
                              checked={selectedCategories?.includes(category?.id)}
                              onChange={() => handleCategoryToggle(category?.id)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                                <span className="text-sm font-medium text-foreground">
                                  {category?.label}
                                </span>
                                <span className="text-sm font-semibold text-primary data-text whitespace-nowrap">
                                  ₹{category?.amount?.toLocaleString('en-IN')}
                                </span>
                              </div>
                              {selectedCategories?.includes(category?.id) && (
                                <Input
                                  type="number"
                                  placeholder="Enter amount"
                                  value={amounts?.[category?.id] || ''}
                                  onChange={(e) => handleAmountChange(category?.id, e?.target?.value)}
                                  error={errors?.[category?.id]}
                                  disabled={selectedCategories?.length > 1}
                                  className="mt-2"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {errors?.categories && (
                        <p className="text-xs text-error mt-2">{errors?.categories}</p>
                      )}
                    </div>
                  )}

                  {selectedCategories?.length > 0 && (
                    <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                      <div className="flex items-center justify-between">
                        <span className="text-sm md:text-base font-medium text-foreground">
                          Total Amount
                        </span>
                        <span className="text-xl md:text-2xl font-bold text-primary data-text">
                          ₹{totalAmount?.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}

                  {errors?.submit && (
                    <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
                      <Icon name="AlertCircle" size={18} className="text-error flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-error">{errors?.submit}</p>
                    </div>
                  )}

                  <Button
                    variant="default"
                    iconName="Check"
                    iconPosition="left"
                    onClick={handleSubmit}
                    loading={isProcessing}
                    disabled={selectedCategories?.length === 0 || totalAmount === 0}
                    className="w-full"
                  >
                    {isProcessing ? 'Processing Payment...' : 'Process Payment & Generate Receipt'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {showReceipt && (
        <ReceiptGenerator 
          paymentData={paymentData} 
          onClose={handleCloseReceipt}
        />
      )}
    </div>
  );
};

export default QuickCashPayment;